import React, { Component } from 'react';
import isObject from 'lodash/isObject';
import ModalModule from '../modal';
import Utils from '../utils';
import UI from '../ui';

const { Modal, ModalButtonActionType } = ModalModule;
const { WrappedInputTextWithLabel, Link, WrappedButton, WrappedButtonType } = UI;
const { Logger, Communication, Session, withLayoutAndModalContexts, Util } = Utils;

class ForgotPassword extends Component {
	constructor(props) {
		super(props);

		this.state = {
			username: Util.getQueryParamByName('username') || '',
			usernameError: 0,
			securityQuestion1: '',
			securityQuestion1Answer: '',
			securityQuestion1AnswerError: 0,
			securityQuestion2: '',
			securityQuestion2Answer: '',
			securityQuestion2AnswerError: 0,
			showSecurityQuestions: false,
			showSuccess: false,
		};

		Util.removeQueryParamByName(['username']);
	}

	handleInputChange = e => {
		this.setState({
			[e.target.name]: e.target.value,
		});
	};

	onInputTextBlur = e => {
		const { name, value } = e.target;
		const fieldError = `${name}Error`;

		this.setState({
			[fieldError]: value !== '' ? 1 : -1,
		});
	};

	onKeyPressEnter = e => {
		const {
			key,
			target: { value, name },
		} = e;

		if (key === 'Enter' && value !== '') {
			e.preventDefault();

			if (name === 'username') {
				this.continueToSecurityQuestionsButtonClick();
			} else {
				this.continueToSuccessButtonClick();
			}
		}
	};

	hasInputFieldError = () => {
		const {
			usernameError,
			securityQuestion1AnswerError,
			securityQuestion2AnswerError,
		} = this.state;

		if (
			usernameError === -1 ||
			securityQuestion1AnswerError === -1 ||
			securityQuestion2AnswerError === -1
		) {
			return false;
		} else {
			return true;
		}
	};

	displayError = () => {
		const { brandName } = Session.getBrand();
		const {
			modalContext: { showModal, closeModal },
		} = this.props;

		showModal({
			component: ({ modalId }) => (
				<Modal
					className="common-authentication"
					modalId={modalId}
					header="Forgot Password"
					primaryButton={{
						type: ModalButtonActionType.Ok,
						text: 'Close',
						onClick: () => {
							closeModal(modalId);
						},
					}}
				>
					<p>We were unable to process your password reset request.</p>
					<br />
					<p>
						Please try again, or for further assistance, read our{' '}
						<Link
							className="no-text-decoration"
							dataqa="reset-password-help-link"
							onClick={() =>
								Util.openHelpCenter(
									`${
										brandName === 'Stamps.com'
											? '20821907151899'
											: '20822835022875'
									}#UUID-acad6603-3116-300f-ad8d-e1584c41b34a_UUID-1cf6112d-8597-27ab-d4a0-ef7e1b965504`
								)
							}
						>
							reset password help article
						</Link>
						.
					</p>
				</Modal>
			),
		});
	};

	continueToSecurityQuestionsButtonClick = async () => {
		const { username } = this.state;

		Logger.saveActionData('Forgot Password Button Clicked');

		// validate username
		if (this.hasInputFieldError()) {
			// unacceptable characters are &, #, and whitespace (Same as WebReg username validation)
			const hasSpecialChar = username.match('[&#\\s]+');

			if (hasSpecialChar && hasSpecialChar.length > 0) {
				this.displayError();
			} else {
				const response = await Communication.getCodewordQuestions({
					Username: username,
				});
				const data = isObject(response.data) ? response.data : {};

				if (Communication.isSuccessful(response)) {
					if (data.Codeword1Question === '' && data.Codeword2Question === '') {
						this.displayError();
					} else {
						this.setState({
							securityQuestion1: data.Codeword1Question,
							securityQuestion2:
								data.Codeword2Question && data.Codeword2Question.length > 0
									? data.Codeword2Question
									: null,
							showSecurityQuestions: true,
						});
					}
				} else {
					this.displayError();
				}
			}
		}
	};

	continueToSuccessButtonClick = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { username, securityQuestion1Answer, securityQuestion2Answer } = this.state;

		Logger.saveActionData('Forgot Password Button Clicked');

		if (this.hasInputFieldError()) {
			const startPasswordResetResponse = await Communication.startPasswordReset({
				Username: username,
				Codeword1: securityQuestion1Answer,
				Codeword2: securityQuestion2Answer,
			});

			if (Communication.isSuccessful(startPasswordResetResponse)) {
				this.setState({
					showSecurityQuestions: false,
					showSuccess: true,
				});
			} else {
				showError({
					data: {
						title: 'Error',
						description: startPasswordResetResponse.ErrorDescription,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			}
		}
	};

	resendEmail = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { username, securityQuestion1Answer, securityQuestion2Answer } = this.state;

		const startPasswordResetResponse = await Communication.startPasswordReset({
			Username: username,
			Codeword1: securityQuestion1Answer,
			Codeword2: securityQuestion2Answer,
		});

		if (!Communication.isSuccessful(startPasswordResetResponse)) {
			showError({
				data: {
					title: 'Error',
					description: startPasswordResetResponse.ErrorDescription,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});
		}
	};

	onForgotUsernameLinkClick = () => {
		const { handleForgotUsernameLinkClick } = this.props;
		handleForgotUsernameLinkClick();
	};

	onPasswordChangeSuccess = () => {
		// redirect to Auth0 login page
		const { goToSignInView } = this.props;
		goToSignInView();
	};

	goToSignInView = () => {
		const { goToSignInView } = this.props;
		goToSignInView();
	};

	renderSuccessMessage = () => {
		const { isAuth0Winclient, isAuth0Mobile } = this.props;

		if (isAuth0Winclient || isAuth0Mobile) {
			const appContext = isAuth0Winclient ? 'Stamps.com application' : 'mobile app';
			return (
				<>
					<p className="content">
						We've sent an email to the address associated with your account (check your spam folder if you don't see it).
					</p>

					<p className="content">
						First, close this {appContext}, then click the link provided in the email. You'll be prompted to create a new password for your account. You can then launch the {appContext} and log in with your new password.
					</p>
				</>
			);
		}

		return (
			<>
				<div className="primary-header">Check Your Email</div>
				<p className="content">
					If you answered the security questions correctly, we will email a
					password recovery link to the email address connected to your account.
				</p>

				<p className="content">
					Check your spam folder if you cannot find the email after a few minutes.
				</p>
			</>
		);
	}

	render() {
		const brand = Session.getBrand().brandName;
		const { layoutContext } = this.props;
		const {
			username,
			usernameError,
			securityQuestion1,
			securityQuestion1Answer,
			securityQuestion1AnswerError,
			securityQuestion2,
			securityQuestion2Answer,
			securityQuestion2AnswerError,
			showSecurityQuestions,
			showSuccess,
		} = this.state;

		const hasContent = str => str && str.trim().length > 0;

		const { isMobile } = layoutContext;

		if (showSecurityQuestions) {
			return (
				<div className="common-authentication auth-forgot-password-view security-questions-view">
					<div className="form">
						<div className="primary-header">Security questions</div>
						<p className="content">
							Answer the following security questions. The answers are case-sensitive.
						</p>

						<div className="row-container">
							<p className="security-question">{securityQuestion1}</p>
							<WrappedInputTextWithLabel
								showErrorBelow
								isInputInvalid={securityQuestion1AnswerError === -1}
								errorMessage="This field is required"
								className="inputtext"
								uiElementConfig={{
									type: 'password',
									showPasswordTooltip: 'Show',
									hidePasswordTooltip: 'Hide',
									value: securityQuestion1Answer,
									name: 'securityQuestion1Answer',
									onChange: this.handleInputChange,
									onBlur: this.onInputTextBlur,
									onKeyPress: this.onKeyPressEnter,
									required: true,
									dataqa: 'securityquestionanswer1',
									autoFocus: true,
								}}
							/>
						</div>
						{securityQuestion2 && (
							<div className="row-container">
								<p className="security-question">{securityQuestion2}</p>
								<WrappedInputTextWithLabel
									showErrorBelow
									isInputInvalid={securityQuestion2AnswerError === -1}
									errorMessage="This field is required"
									className="inputtext"
									uiElementConfig={{
										type: 'password',
										showPasswordTooltip: 'Show',
										hidePasswordTooltip: 'Hide',
										value: securityQuestion2Answer,
										name: 'securityQuestion2Answer',
										onChange: this.handleInputChange,
										onBlur: this.onInputTextBlur,
										onKeyPress: this.onKeyPressEnter,
										required: true,
										dataqa: 'securityquestionanswer2',
									}}
								/>
							</div>
						)}

						<div className="row-container centered">
							<WrappedButton
								label="Continue"
								onClick={this.continueToSuccessButtonClick}
								buttonType={WrappedButtonType.Primary}
								dataqa="continue-security-questions"
								disabled={
									!(
										hasContent(securityQuestion1Answer) &&
										(!securityQuestion2 || hasContent(securityQuestion2Answer))
									)
								}
							/>
						</div>
					</div>
				</div>
			);
		} else if (showSuccess) {
			return (
				<div className="common-authentication auth-forgot-password-view success-message-view">
					<div className="form">
						{this.renderSuccessMessage()}
						

						<p className="content">
							For further help, visit our{' '}
							<Link
								className="no-text-decoration"
								dataqa="reset-password-help-link"
								onClick={() =>
									Util.openHelpCenter(
										`${
											brand === 'Stamps.com'
												? '20821907151899'
												: '20822835022875'
										}#UUID-acad6603-3116-300f-ad8d-e1584c41b34a_UUID-1cf6112d-8597-27ab-d4a0-ef7e1b965504`
									)
								}
							>
								password reset help article
							</Link>{' '}
						</p>
						<div className="row-container centered">
							<WrappedButton
								label="Return to Login"
								buttonType={WrappedButtonType.Primary}
								dataqa="return-to-success-message"
								onClick={this.onPasswordChangeSuccess}
							/>
						</div>
						<div className="row-container centered">
							<WrappedButton
								label="Resend email"
								buttonType={WrappedButtonType.Secondary}
								dataqa="resend-email-success-message"
								onClick={this.resendEmail}
							/>
						</div>
					</div>
				</div>
			);
		} else {
			return (
				<div className="common-authentication auth-forgot-password-view">
					<div className="form">
						<div className="primary-header">Reset Password</div>
						<p className="content">Enter your {brand} account username.</p>
						<div className="row-container">
							<WrappedInputTextWithLabel
								showErrorBelow
								isInputInvalid={usernameError === -1}
								errorMessage="This field is required"
								className="inputtext"
								label="Username"
								uiElementConfig={{
									type: 'text',
									name: 'username',
									value: username,
									onChange: this.handleInputChange,
									onBlur: this.onInputTextBlur,
									onKeyPress: this.onKeyPressEnter,
									required: true,
									dataqa: 'forgotpasswordusername',
									autoFocus: true,
								}}
							/>
							<p className="username-link">
								<Link
									onClick={this.onForgotUsernameLinkClick}
									dataqa="forgot-username-link"
								>
									Forgot username?
								</Link>
							</p>
						</div>

						<div className="row-container centered">
							<WrappedButton
								label="Continue"
								buttonType={WrappedButtonType.Primary}
								dataqa="continue-forgot-password"
								onClick={this.continueToSecurityQuestionsButtonClick}
								disabled={!hasContent(username)}
							/>
						</div>
						<div className="row-container centered">
							<WrappedButton
								label="Cancel"
								buttonType={WrappedButtonType.TertiaryGhost}
								dataqa="cancel-forgot-password"
								onClick={this.goToSignInView}
							/>
						</div>
					</div>
				</div>
			);
		}
	}
}

export default withLayoutAndModalContexts(ForgotPassword);
